<script lang="ts">
    import VanVolxemLogo from '$lib/assets/vectors/VanVolxemLogo.svelte';
    import CartIcon from '$lib/assets/icons/CartIcon.svelte';
    import { t } from 'svelte-i18n';
    import Dropdown from '$lib/components/navigation/Dropdown.svelte';
    import { useLocaleContext, useShopContext } from '$lib/context';
    import ProfileIcon from '$lib/assets/icons/ProfileIcon.svelte';
    import Search from '$lib/components/navigation/Search.svelte';
    import type { ProductCollection } from '$lib/api/types/typesenseApiTypes';
    import type { SearchResponse } from 'typesense/lib/Typesense/Documents';
    import { fly } from 'svelte/transition';
    import CartSideBar from '$lib/components/navigation/CartSideBar.svelte';
    import AccountMenu from '$lib/components/account/AccountMenu.svelte';
    import { disableGlobalScroll } from '$lib/util/util';

    const lang = useLocaleContext();
    const shopContext = useShopContext();

    export let cartLineItemAmount: number;

    export let dropdownActive: boolean;

    let innerWidth: number;
    $: isMobile = innerWidth < 1024;

    let searchOpen: boolean;
    $: if (!isMobile || dropdownActive) searchOpen = false;
    let searchResult: SearchResponse<ProductCollection>;
    let showHints: boolean;

    let cartSideBarOpen: boolean;

    // $: disableGlobalScroll(dropdownActive || cartSideBarOpen);
    $: disableGlobalScroll(cartSideBarOpen || (dropdownActive && innerWidth < 1024));

    let accountMenuOpen: boolean = false;
    let accountMenuMargin: number;
</script>

<svelte:window bind:innerWidth />

<nav bind:clientHeight={accountMenuMargin} class="navbar px-0 z-50 fixed flex justify-center w-full bg-black">
    <div
        class="text-white h-24 lg:h-auto grid gap-y-5 lg:gap-y-0 mt-1 mb-1.5 grid-cols-2 lg:flex items-center font-jost max-w-screen-2xl w-full px-5"
    >
        <div class="flex col-span-2 z-10 w-full flex-1 justify-between lg:justify-start gap-3 items-center">
            <Dropdown bind:dropdownActive bind:cartSideBarOpen />
            <div class="hidden lg:flex justify-start gap-3 items-center">
                <a
                    class="text-neutral-500 hover:text-neutral-400 transition-colors"
                    on:click={() => (dropdownActive = false)}
                    href="https://vanvolxem.com/"
                >
                    {$t('shop.navigation.winery')}</a
                >
                <div class="bg-primary w-[1px] h-4 opacity-50" />
                <a class="text-offwhite" on:click={() => (dropdownActive = false)} href="/{lang}/"
                    >{$t('shop.navigation.shop')}</a
                >
            </div>
            {#if searchOpen && isMobile}
                <div transition:fly={{ x: 20, duration: 300 }}>
                    <Search
                        bind:searchResult
                        bind:showHints
                        inputClasses="bg-transparent outline-none w-56 placeholder:text-chamois"
                        buttonClasses="size-10 bg-primary flex justify-center items-center text-black"
                        iconWidth={17}
                        iconStrokeWidth={1.5}
                        searchHintOptions={{
                            enableSearchHints: true,
                            containerClass: 'absolute bottom-0 right-0 h-0',
                            class: 'mt-[0.5rem] w-[16.5rem]'
                        }}
                    />
                </div>
            {/if}
        </div>
        {#if !searchOpen || !isMobile}
            <div class="absolute left-0 top-[18px] w-full lg:w-auto lg:flex-1 lg:static flex justify-center">
                <a
                    transition:fly={{ x: -20, duration: 300 }}
                    href="/{lang}"
                    class="min-h-10 z-20 w-fit flex justify-center items-center"
                    on:click={() => (dropdownActive = false)}
                >
                    <div class="-translate-y-1 lg:translate-y-0">
                        <VanVolxemLogo />
                    </div>
                </a>
            </div>
        {/if}
        <div class="row-start-2 lg:hidden flex justify-start gap-3 items-center">
            <a on:click={() => (dropdownActive = false)} class="text-neutral-500" href="/{lang}/"
                >{$t('shop.navigation.winery')}</a
            >
            <div class="bg-primary w-[1px] h-4 opacity-50" />
            <a on:click={() => (dropdownActive = false)} href="/{lang}/">{$t('shop.navigation.shop')}</a>
        </div>
        <div class="row-start-2 h-10 flex-1 flex justify-end items-center gap-7 lg:gap-6">
            <div class="h-full hidden lg:flex flex-col group">
                <Search
                    primarySearch
                    bind:searchResult
                    class="h-full flex items-center"
                    inputClasses="bg-transparent outline-none mr-3 w-56 placeholder:text-neutral-500 placeholder:hover:text-neutral-400 placeholder:transition-colors placeholder:duration-200"
                    buttonClasses=" flex justify-end items-center text-offwhite hover:text-primary transition-colors"
                    iconWidth={25}
                    searchHintOptions={{ enableSearchHints: true }}
                />
            </div>
            <button
                class="block lg:hidden"
                on:click={() => {
                    searchOpen = !searchOpen;
                }}
            >
                {#if searchOpen}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        viewBox="0 0 26 27"
                        class="fill-none stroke-offwhite"
                    >
                        <circle cx="10.7152" cy="10.6863" r="9.9051" />
                        <path d="M6.79883 6.29883L15 14.5" />
                        <path d="M6.79883 14.5L15 6.29883" />
                        <path d="M17.1057 18.5039L24.8961 26.2943" />
                    </svg>
                {:else}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        viewBox="0 0 26 27"
                        class="fill-none stroke-offwhite"
                    >
                        <circle cx="10.7152" cy="10.6863" r="9.9051" />
                        <path d="M17.1057 18.5039L24.8961 26.2943" />
                    </svg>
                {/if}
            </button>

            <div>
                {#if shopContext?.storeContext?.customer}
                    <button
                        class="flex items-center text-offwhite hover:text-primary transition-colors"
                        on:click|stopPropagation={() => {
                            dropdownActive = false;
                            cartSideBarOpen = false;
                            accountMenuOpen = !accountMenuOpen;
                        }}
                    >
                        <ProfileIcon />
                    </button>
                {:else}
                    <a
                        href="/{lang}/login"
                        on:click={() => (dropdownActive = false)}
                        class="text-offwhite hover:text-primary transition-colors"
                    >
                        <ProfileIcon />
                    </a>
                {/if}
            </div>

            <button
                class="relative ml-1 text-offwhite hover:text-primary transition-colors"
                on:click|stopPropagation={() => {
                    dropdownActive = false;
                    accountMenuOpen = false;
                    cartSideBarOpen = !cartSideBarOpen;
                }}
            >
                <CartIcon width="20" />
                {#if cartLineItemAmount}
                    <div
                        class="absolute h-3.5 px-0.5 text-black justify-center flex items-center text-sm bg-primary -left-2 top-5"
                    >
                        {cartLineItemAmount}
                    </div>
                {/if}
            </button>
        </div>
    </div>
</nav>
<CartSideBar bind:cartSideBarOpen />
<AccountMenu bind:accountMenuOpen marginTop={accountMenuMargin} />
